/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect, FocusEventHandler, useMemo } from "react";
import { createPortal } from "react-dom";

import "./Dropdown.scss";
import { ReactComponent as CaretUp } from "assets/images/icons/CaretUp.svg";

interface IDropdownOptions {
  label: any;
  value: string | number;
  is_active?: boolean;
  children?: Array<any>;
}

export type DropdownCommonProps = {
  options: IDropdownOptions[];
  value: any;
  onChange?: Function;
  name?: string;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  placeholder?: string;
  className?: string;
  hasError?: boolean;
  customLabel?: (customLabelOptions: any) => JSX.Element;
  canDeselect?: boolean;
  activeMode?: boolean;
  setUpdate?: Function;
  disabled?: boolean;
  dataTestid?: string;
  usePortal?: boolean;
  dropdownClassName?: string;
};

type ActionProps = Omit<IDropdownOptions, "is_active">;

function DropdownCommon({
  options,
  value,
  className,
  name,
  onBlur,
  hasError,
  placeholder,
  customLabel,
  canDeselect,
  activeMode,
  setUpdate,
  onChange = () => {},
  disabled,
  dataTestid,
  usePortal,
  dropdownClassName,
}: DropdownCommonProps) {
  const [isActive, setIsActive] = useState<boolean>(false);
  const ref = React.useRef<HTMLInputElement>(null);
  const portalRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});
  const hasChildren = useMemo(() => options.some((item) => item?.children?.length), [options]);

  useEffect(() => {
    if (!usePortal || !isActive || !triggerRef.current) return;

    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setPortalStyle({
        position: "absolute",
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 1110,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [usePortal, isActive]);

  useEffect(() => {
    const checkIfClickedOutside = (e: any) => {
      if (
        isActive &&
        ref?.current &&
        !ref?.current.contains(e.target) &&
        (!portalRef?.current || !portalRef.current.contains(e.target))
      ) {
        setIsActive(false);
      }
    };
    document.addEventListener("click", checkIfClickedOutside);
    return () => {
      document.removeEventListener("click", checkIfClickedOutside);
    };
  }, [isActive]);

  const handleSelected = (item: ActionProps) => {
    if (disabled) return;
    onChange(item);
    if (setUpdate) {
      setUpdate(item.value);
    }
    setIsActive(false);
  };

  return (
    <div
      className={`drop-down-size ${isActive || activeMode ? "active-dropdown" : ""} ${
        hasChildren ? "with-child" : ""
      } ${className}`}
      ref={ref}
      id={name}
      data-testid={dataTestid}
    >
      <div className="dropdown-main">
        <div
          ref={triggerRef}
          onClick={() => {
            if (disabled) return;
            setIsActive(!isActive);
          }}
          style={{
            zIndex: isActive ? 1115 : 0,
          }}
          className={`dropdown-widget-btn cursor-pointer pt-9 pb-9 ${isActive || activeMode ? "active-dropdown" : ""}`}
        >
          {customLabel ? (
            React.cloneElement(
              customLabel({ label: options.find((item) => item.value === value)?.label || "", value, isActive, hasError }),
              {
                onBlur,
                name,
              },
            )
          ) : (
            <input
              type="text"
              className={`font-b3 form-control bg-white br-10 pt-6 pb-6 pr-20 w-100 height-auto placeholder-light ${
                isActive ? "search-select-input" : "dropdown-hover"
              } ${hasError ? "invalid-input" : ""}`}
              value={options.find((item) => item.value === value)?.label || value}
              placeholder={placeholder}
              readOnly
              name={name}
              onBlur={onBlur}
              disabled={disabled}
            />
          )}

          <span className="drop-down-menu">
            <CaretUp
              className={`position-absolute align-self-center ${isActive ? "invert-color" : ""}`}
              style={{
                transition: "all .5s",
                transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                marginTop: -1,
                zIndex: isActive ? 99999 : 0,
              }}
            />
          </span>
        </div>
        {(() => {
          const dropdownSection = (
            <div
              ref={usePortal ? portalRef : undefined}
              className={`dropdown-section${usePortal ? " dropdown-section--portal" : ""}${dropdownClassName ? ` ${dropdownClassName}` : ""}`}
              style={
                usePortal
                  ? { display: isActive ? "block" : "none", ...portalStyle }
                  : { display: isActive ? "block" : "none", zIndex: isActive ? 1110 : 0 }
              }
            >
              <div className="dropdown-widget-content">
                {options?.map((item: IDropdownOptions, index) => (
                  <div
                    key={item?.value?.toString() + index?.toString()}
                    onClick={() => handleSelected(item)}
                    className="item cursor-pointer position-relative font-b3 d-flex align-items-center justify-content-between"
                  >
                    {item?.label}
                    {item?.children?.length && (
                      <>
                        <CaretUp
                          style={{
                            transform: "rotate(30deg)",
                          }}
                        />
                        <div className="sub-item">
                          {item.children.map((child: any, index) => (
                            <div
                              key={child.value?.toString() + index?.toString()}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelected(child);
                              }}
                              className="item cursor-pointer position-relative font-b3"
                            >
                              {child.label}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ))}
                {canDeselect && (
                  <div
                    onClick={() => handleSelected({ label: "", value: "" })}
                    className="item cursor-pointer position-relative text-center"
                  >
                    Deselect
                  </div>
                )}
              </div>
            </div>
          );

          return usePortal ? createPortal(dropdownSection, document.body) : dropdownSection;
        })()}
      </div>
    </div>
  );
}

export default DropdownCommon;

DropdownCommon.defaultProps = {
  className: "",
  customLabel: undefined,
  canDeselect: false,
  activeMode: false,
  hasError: false,
  placeholder: "",
  name: "",
  onBlur: undefined,
  setUpdate: undefined,
  onChange: () => {},
  disabled: false,
  dataTestid: undefined,
  usePortal: false,
  dropdownClassName: "",
};
